import DataLoader from "dataloader";

import { buildPermissionCacheKey } from "src/components/permission/build-cache-key";
import {
  PermissionsBatchV2Document,
  type PermissionsBatchV2Query,
  type PermissionsBatchV2QueryVariables,
  type PermissionsV2QueryVariables,
} from "src/graphql/types";

import { fetcher } from "./fetcher";

const batchFn = async (
  keys: readonly PermissionsV2QueryVariables[],
): Promise<boolean[]> => {
  const inputs = keys.map((k) => ({ v2: k.v2 ?? null }));
  const data = await fetcher<
    PermissionsBatchV2Query,
    PermissionsBatchV2QueryVariables
  >(PermissionsBatchV2Document, { inputs })();
  return data.isResourcesAuthorized.map((r) => Boolean(r?.isAuthorized));
};

// Per-tick loader: instantiated lazily on first call, discarded after the
// microtask flush. RQ remains the across-tick cache; this loader exists only
// to coalesce identical-tick calls and batch one network round trip.
let loader: DataLoader<PermissionsV2QueryVariables, boolean> | null = null;

export const load = (
  variables: PermissionsV2QueryVariables,
): Promise<boolean> => {
  if (loader === null) {
    loader = new DataLoader(batchFn, { cacheKeyFn: buildPermissionCacheKey });
    queueMicrotask(() => {
      loader = null;
    });
  }
  const promise = loader.load(variables);
  // Attach a no-op catch so rejections aren't reported as "unhandled" before
  // the caller has a chance to attach their own handler.
  promise.catch(() => {});
  return promise;
};
